import React, { useState, useEffect } from 'react';
import { ArrowLeft, Edit2, Trash2, User, Mail, Calendar, Clock, Shield, Building, Users, Briefcase, Plus, X, CheckCircle, XCircle, FolderOpen, AlertCircle } from 'lucide-react';
import { User as UserType, UserRole, Client, Project } from '../../../types';
import { UsersService } from '../../../services/api/users';
import { ClientsService } from '../../../services/api/client';
import projectService from '../../../services/projectService';
import { useAuth } from '../../../contexts/AuthContext';
import UserForm from '../UserForm/UserForm';
import { Button, Badge, Card, EmptyState, LoadingSpinner } from '../../common';

interface UserDetailProps {
  user: UserType;
  onBack: () => void;
  onUserUpdated: () => void;
}

interface ClientAssignment {
  assignment_id: number;
  client_id: number;
  company_name: string;
  contact_name: string;
  email: string;
  status: string;
  role: string;
  hourly_rate?: number;
  added_at: string;
}

interface ProjectAssignment {
  assignment_id: number;
  project_id: number;
  project_name: string;
  project_status: string;
  priority: string;
  client_name: string;
  role: string;
  hourly_rate?: number;
  added_at: string;
}

const UserDetail: React.FC<UserDetailProps> = ({ user, onBack, onUserUpdated }) => {
  const { token } = useAuth();
  const [userData, setUserData] = useState<UserType>(user);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientAssignments, setClientAssignments] = useState<ClientAssignment[]>([]);
  const [projectAssignments, setProjectAssignments] = useState<ProjectAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Assignment modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [freshClientAssignments, setFreshClientAssignments] = useState<ClientAssignment[]>([]);
  const [freshProjectAssignments, setFreshProjectAssignments] = useState<ProjectAssignment[]>([]);

  useEffect(() => {
    loadRoles();
    loadAssignments();
  }, []);

  // Update local userData when the user prop changes
  useEffect(() => {
    setUserData(user);
  }, [user]);

  const loadRoles = async () => {
    try {
      const rolesData = await UsersService.getRoles(token || undefined);
      setRoles(rolesData);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const loadAssignments = async () => {
    setLoadingAssignments(true);
    try {
      // Load client assignments
      const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userData.id}/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setClientAssignments(clientData);
      }

      // Load project assignments
      const projectResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userData.id}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (projectResponse.ok) {
        const projectData = await projectResponse.json();
        setProjectAssignments(projectData);
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleDeleteUser = async () => {
    if (window.confirm(`Are you sure you want to delete ${userData.first_name} ${userData.last_name}?`)) {
      try {
        setLoading(true);
        await UsersService.deleteUser(userData.id, token || undefined);
        onUserUpdated();
        onBack();
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('Failed to delete user');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditSuccess = async () => {
    setShowEditForm(false);

    // Refresh the current user's data
    try {
      const updatedUser = await UsersService.getUserById(userData.id, token || undefined);
      setUserData(updatedUser);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }

    // Also refresh the parent component's user list
    onUserUpdated();
  };

  const loadClientsAndProjects = async () => {
    try {
      const [clients, projects] = await Promise.all([
        ClientsService.getClients(token || undefined),
        projectService.getProjects()
      ]);

      setAvailableClients(Array.isArray(clients) ? clients : []);
      setAvailableProjects(Array.isArray(projects) ? projects : []);
    } catch (error) {
      console.error('Failed to load clients and projects:', error);
      setAvailableClients([]);
      setAvailableProjects([]);
    }
  };

  const handleAssignToClient = async (clientId: number, role: string, hourlyRate?: number) => {
    setAssignmentLoading(true);
    try {
      await UsersService.assignUserToClient(userData.id, clientId, role, hourlyRate, token || undefined);
      await loadAssignments(); // Refresh assignments
      setShowClientModal(false);
    } catch (error) {
      console.error('Failed to assign user to client:', error);
      alert('Failed to assign user to client: ' + (error as Error).message);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignToProject = async (projectId: number, role: string, hourlyRate?: number) => {
    setAssignmentLoading(true);
    try {
      await UsersService.assignUserToProject(userData.id, projectId, role, hourlyRate, token || undefined);
      await loadAssignments(); // Refresh assignments
      setShowProjectModal(false);
    } catch (error) {
      console.error('Failed to assign user to project:', error);
      alert('Failed to assign user to project: ' + (error as Error).message);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveFromClient = async (clientId: number) => {
    if (window.confirm('Are you sure you want to remove this client assignment?')) {
      try {
        await UsersService.removeUserFromClient(userData.id, clientId, token || undefined);
        await loadAssignments(); // Refresh assignments
      } catch (error) {
        console.error('Failed to remove user from client:', error);
        alert('Failed to remove user from client');
      }
    }
  };

  const handleRemoveFromProject = async (projectId: number) => {
    if (window.confirm('Are you sure you want to remove this project assignment?')) {
      try {
        await UsersService.removeUserFromProject(userData.id, projectId, token || undefined);
        await loadAssignments(); // Refresh assignments
      } catch (error) {
        console.error('Failed to remove user from project:', error);
        alert('Failed to remove user from project');
      }
    }
  };

  const openClientModal = async () => {
    // Load fresh data for the modal
    const [clients, projects] = await Promise.all([
      ClientsService.getClients(token || undefined),
      projectService.getProjects()
    ]);

    // Load fresh assignments
    const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userData.id}/clients`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const freshClientAssignments = clientResponse.ok ? await clientResponse.json() : [];


    setAvailableClients(Array.isArray(clients) ? clients : []);
    setFreshClientAssignments(freshClientAssignments);
    setShowClientModal(true);
  };

  const openProjectModal = async () => {
    // Load fresh data for the modal
    const [clients, projects] = await Promise.all([
      ClientsService.getClients(token || undefined),
      projectService.getProjects()
    ]);

    // Load fresh project assignments
    const projectResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userData.id}/projects`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const freshProjectAssignments = projectResponse.ok ? await projectResponse.json() : [];


    setAvailableProjects(Array.isArray(projects) ? projects : []);
    setFreshProjectAssignments(freshProjectAssignments);
    setShowProjectModal(true);
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Mock stats - would come from API in real implementation
  const stats = [
    { icon: Clock, label: 'Hours Logged', value: '124h', color: 'bg-purple-100', iconColor: 'text-purple-600' },
    { icon: CheckCircle, label: 'Tasks Completed', value: '18', color: 'bg-green-100', iconColor: 'text-green-600' },
    { icon: FolderOpen, label: 'Active Projects', value: projectAssignments.length.toString(), color: 'bg-blue-100', iconColor: 'text-blue-600' },
    { icon: AlertCircle, label: 'Efficiency', value: '94%', color: 'bg-pink-100', iconColor: 'text-pink-600' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Team</span>
              </button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="md"
                icon={<Edit2 className="w-4 h-4" />}
                onClick={() => setShowEditForm(true)}
              >
                Edit User
              </Button>
              <Button
                variant="danger"
                size="md"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={handleDeleteUser}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Profile Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              {userData.profile_image_url ? (
                <img
                  className="w-24 h-24 rounded-full"
                  src={`${process.env.REACT_APP_API_URL}${userData.profile_image_url}`}
                  alt={`${userData.first_name || ''} ${userData.last_name || ''}`}
                />
              ) : (
                <span className="text-3xl font-bold text-indigo-600">
                  {userData.first_name?.[0] || '?'}{userData.last_name?.[0] || '?'}
                </span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {userData.display_name || `${userData.first_name} ${userData.last_name}`}
                </h1>
                <Badge status={userData.status} />
              </div>
              <p className="text-gray-600 mb-4">{userData.email}</p>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Basic Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Full Name:</span>
                      <span className="text-gray-900 font-medium">
                        {userData.display_name || `${userData.first_name} ${userData.last_name}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Email:</span>
                      <span className="text-gray-900 font-medium">{userData.email}</span>
                    </div>
                    {userData.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900 font-medium">{userData.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <div className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${userData.email_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {userData.email_verified ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {userData.email_verified ? 'Verified' : 'Not Verified'}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Professional Information</h3>
                  <div className="space-y-2">
                    {userData.department && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Department:</span>
                        <span className="text-gray-900 font-medium">{userData.department}</span>
                      </div>
                    )}
                    {userData.job_title && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Job Title:</span>
                        <span className="text-gray-900 font-medium">{userData.job_title}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Roles:</span>
                      <div className="flex flex-wrap gap-1">
                        {(userData as any).roles ? (
                          typeof (userData as any).roles === 'string' ? (
                            (userData as any).roles.split(',').map((role: string, index: number) => (
                              <span key={index} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                {role.trim()}
                              </span>
                            ))
                          ) : Array.isArray((userData as any).roles) ? (
                            (userData as any).roles.map((role: any, index: number) => (
                              <span key={index} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                {role.display_name || role.name || role}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )
                        ) : (
                          <span className="text-sm text-gray-500">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <Card key={index}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Account Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Account Activity
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Created</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-900 font-medium">{formatDate(userData.created_at)}</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Last Login</p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-900 font-medium">{formatDate(userData.last_login_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Client Assignments */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Client Assignments
            </h2>
            <Button
              variant="primary"
              size="md"
              icon={<Plus className="w-4 h-4" />}
              onClick={openClientModal}
            >
              Assign to Client
            </Button>
          </div>

          {loadingAssignments ? (
            <LoadingSpinner message="Loading assignments..." />
          ) : clientAssignments.length > 0 ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Added</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clientAssignments.map((assignment) => (
                    <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{assignment.company_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{assignment.role.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {assignment.hourly_rate ? `$${(typeof assignment.hourly_rate === 'number' ? assignment.hourly_rate : parseFloat(assignment.hourly_rate as any)).toFixed(2)}/hr` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatShortDate(assignment.added_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveFromClient(assignment.client_id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={Users}
              title="No client assignments yet"
              description=""
            />
          )}
        </div>

        {/* Project Assignments */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Project Assignments
            </h2>
            <Button
              variant="primary"
              size="md"
              icon={<Plus className="w-4 h-4" />}
              onClick={openProjectModal}
            >
              Assign to Project
            </Button>
          </div>

          {loadingAssignments ? (
            <LoadingSpinner message="Loading assignments..." />
          ) : projectAssignments.length > 0 ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Added</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {projectAssignments.map((assignment) => (
                    <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{assignment.project_name}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{assignment.client_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">{assignment.role}</td>
                      <td className="px-4 py-3">
                        <Badge status={assignment.project_status} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge status={assignment.priority} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatShortDate(assignment.added_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveFromProject(assignment.project_id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={FolderOpen}
              title="No project assignments yet"
              description=""
            />
          )}
        </div>
      </div>

      {/* Edit Form Modal */}
      {showEditForm && (
        <UserForm
          user={userData}
          roles={roles}
          onSuccess={handleEditSuccess}
          onCancel={() => setShowEditForm(false)}
        />
      )}

      {/* Assign to Client Modal */}
      {showClientModal && (
        <ClientAssignmentModal
          availableClients={availableClients.filter(client =>
            (client.status === 'active' || !client.status || (client.status as string) === '') &&
            !freshClientAssignments.some(assignment => assignment.client_id === client.id)
          )}
          onAssign={handleAssignToClient}
          onCancel={() => setShowClientModal(false)}
          loading={assignmentLoading}
        />
      )}

      {/* Assign to Project Modal */}
      {showProjectModal && (
        <ProjectAssignmentModal
          availableProjects={availableProjects.filter(project =>
            !freshProjectAssignments.some(assignment => assignment.project_id === project.id)
          )}
          onAssign={handleAssignToProject}
          onCancel={() => setShowProjectModal(false)}
          loading={assignmentLoading}
        />
      )}
    </div>
  );
};

// Client Assignment Modal Component
interface ClientAssignmentModalProps {
  availableClients: Client[];
  onAssign: (clientId: number, role: string, hourlyRate?: number) => void;
  onCancel: () => void;
  loading: boolean;
}

const ClientAssignmentModal: React.FC<ClientAssignmentModalProps> = ({
  availableClients,
  onAssign,
  onCancel,
  loading
}) => {
  const [selectedClient, setSelectedClient] = useState<number | ''>('');
  const [role, setRole] = useState('contact');
  const [hourlyRate, setHourlyRate] = useState<number | ''>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedClient) {
      onAssign(Number(selectedClient), role, hourlyRate ? Number(hourlyRate) : undefined);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Assign to Client</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Client
            </label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value === '' ? '' : Number(e.target.value))}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">
                {availableClients.length === 0 ? 'No available clients to assign' : 'Choose a client...'}
              </option>
              {availableClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {(client as any).company_name || client.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="contact">Contact</option>
              <option value="account_manager">Account Manager</option>
              <option value="project_manager">Project Manager</option>
              <option value="designer">Designer</option>
              <option value="developer">Developer</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hourly Rate (optional)
            </label>
            <input
              type="number"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="e.g. 75"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              size="md"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={loading || !selectedClient || availableClients.length === 0}
            >
              {loading ? 'Assigning...' : availableClients.length === 0 ? 'No Clients Available' : 'Assign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Project Assignment Modal Component
interface ProjectAssignmentModalProps {
  availableProjects: Project[];
  onAssign: (projectId: number, role: string, hourlyRate?: number) => void;
  onCancel: () => void;
  loading: boolean;
}

const ProjectAssignmentModal: React.FC<ProjectAssignmentModalProps> = ({
  availableProjects,
  onAssign,
  onCancel,
  loading
}) => {
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [role, setRole] = useState('designer');
  const [hourlyRate, setHourlyRate] = useState<number | ''>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedProject) {
      onAssign(Number(selectedProject), role, hourlyRate ? Number(hourlyRate) : undefined);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Assign to Project</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value === '' ? '' : Number(e.target.value))}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">
                {availableProjects.length === 0 ? 'No available projects to assign' : 'Choose a project...'}
              </option>
              {availableProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="designer">Designer</option>
              <option value="developer">Developer</option>
              <option value="project_manager">Project Manager</option>
              <option value="account_manager">Account Manager</option>
              <option value="copywriter">Copywriter</option>
              <option value="qa_tester">QA Tester</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hourly Rate (optional)
            </label>
            <input
              type="number"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="e.g. 75"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              size="md"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={loading || !selectedProject || availableProjects.length === 0}
            >
              {loading ? 'Assigning...' : availableProjects.length === 0 ? 'No Projects Available' : 'Assign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDetail;